import type { VerbatimRespondent } from '../data/verbatimData';
import { extractThemes, type ThemeResult } from './verbatimThemes';

export interface BuyerProxyResult {
  respondents: VerbatimRespondent[];
  count: number;
  demographics: {
    femalePercent: number;
    malePercent: number;
    medianAge: number;
    ageRange: [number, number];
    topIncomes: { label: string; percent: number }[];
    topRegions: { label: string; percent: number }[];
    topGoals: ThemeResult[];
    topWtpFactors: ThemeResult[];
    brandAwareness: { neverHeardPercent: number; positivePercent: number; negativePercent: number };
  };
  conjointBehavior: {
    avgNoneRate: number;
    avgChosenPrice: number | null;
    topFeatures: { name: string; rate: number }[];
  };
}

function median(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

function topN(items: string[], n: number): { label: string; percent: number }[] {
  const counts: Record<string, number> = {};
  for (const item of items) if (item) counts[item] = (counts[item] || 0) + 1;
  const total = items.filter(Boolean).length || 1;
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([label, count]) => ({ label, percent: Math.round(count / total * 100) }));
}

export function computeBuyerProxy(
  allRespondents: VerbatimRespondent[],
  packagePrice: number,
  enabledFeatures: string[]
): BuyerProxyResult {
  const priceKey = String(packagePrice);

  // Step 1: exclude always-none
  const eligible = allRespondents.filter(r => r.segment !== 'always-none');

  // Step 2: filter to respondents who chose products at this price in >= 30% of their product choices
  const priceAligned = eligible.filter(r => {
    const total = Object.values(r.priceDistribution).reduce((s, v) => s + v, 0);
    if (total === 0) return false;
    const atPrice = r.priceDistribution[priceKey] || 0;
    return atPrice / total >= 0.3;
  });

  // Step 3: score by feature alignment — how often they chose concepts with the package's features
  const scored = priceAligned.map(r => {
    let featureScore = 0;
    for (const f of enabledFeatures) {
      featureScore += r.featureChoiceRates[f] || 0;
    }
    return { respondent: r, score: featureScore };
  }).sort((a, b) => b.score - a.score);

  // Step 4: take top 50% (at least 20 respondents for meaningful stats)
  const cutoff = Math.max(20, Math.ceil(scored.length / 2));
  const proxyRespondents = scored.slice(0, cutoff).map(s => s.respondent);

  // If too few pass the price filter, fall back to all non-always-none
  const finalGroup = proxyRespondents.length >= 15 ? proxyRespondents : eligible.slice(0, 200);
  const n = finalGroup.length;

  // Demographics
  const femaleCount = finalGroup.filter(r => r.gender === 'Female').length;
  const maleCount = finalGroup.filter(r => r.gender === 'Male').length;
  const ages = finalGroup.map(r => r.age).filter(a => a > 0);

  // Brand awareness
  const brandThemes = extractThemes(finalGroup, 'brandPerception');
  const neverHeard = brandThemes.find(t => t.theme === 'unknown');
  const positive = brandThemes.find(t => t.theme === 'positive');
  const negative = brandThemes.find(t => t.theme === 'negative');

  // Conjoint behavior
  const avgNone = n > 0 ? +(finalGroup.reduce((s, r) => s + r.noneCount, 0) / n).toFixed(1) : 0;
  const withPrices = finalGroup.filter(r => r.avgChosenPrice !== null);
  const avgPrice = withPrices.length > 0
    ? Math.round(withPrices.reduce((s, r) => s + r.avgChosenPrice!, 0) / withPrices.length)
    : null;

  // Top features by choice rate
  const featureSums: Record<string, number> = {};
  const featureCounts: Record<string, number> = {};
  for (const r of finalGroup) {
    for (const [f, rate] of Object.entries(r.featureChoiceRates)) {
      featureSums[f] = (featureSums[f] || 0) + rate;
      featureCounts[f] = (featureCounts[f] || 0) + 1;
    }
  }
  const topFeatures = Object.entries(featureSums)
    .map(([name, sum]) => ({ name, rate: +(sum / (featureCounts[name] || 1)).toFixed(2) }))
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 5);

  return {
    respondents: finalGroup,
    count: n,
    demographics: {
      femalePercent: n > 0 ? Math.round(femaleCount / n * 100) : 0,
      malePercent: n > 0 ? Math.round(maleCount / n * 100) : 0,
      medianAge: ages.length > 0 ? median(ages) : 0,
      ageRange: ages.length > 0 ? [Math.min(...ages), Math.max(...ages)] as [number, number] : [0, 0],
      topIncomes: topN(finalGroup.map(r => r.income), 3),
      topRegions: topN(finalGroup.map(r => r.region), 4),
      topGoals: extractThemes(finalGroup, 'goals').slice(0, 4),
      topWtpFactors: extractThemes(finalGroup, 'wtpFactors').slice(0, 4),
      brandAwareness: {
        neverHeardPercent: neverHeard?.percentage ?? 0,
        positivePercent: positive?.percentage ?? 0,
        negativePercent: negative?.percentage ?? 0,
      },
    },
    conjointBehavior: {
      avgNoneRate: avgNone,
      avgChosenPrice: avgPrice,
      topFeatures,
    },
  };
}
